import { useEffect, useRef, useState } from "react";
import gsap from "gsap";

const PHASES = [
  { text: "Building your dream retirement", sub: "Gathering your selections" },
  { text: "Calculating future lifestyle cost", sub: "Compounding the numbers" },
  { text: "Adjusting for inflation", sub: "6% applied across the years" },
  { text: "Finding the smartest investment path", sub: "Optimising at 15% expected return" },
];

const rand = (min, max) => Math.random() * (max - min) + min;

// Generate a random "crunching" rupee string that NEVER reveals the actual answer.
// Cycles through plausible-looking Cr values in a wide range.
const randomScrambleValue = () => {
  const cr = rand(1.2, 48); // 1.2 Cr → 48 Cr, intentionally varied
  const decimals = Math.random() > 0.4 ? 2 : 0;
  return `₹${cr.toFixed(decimals)}`;
};

const randomScrambleUnit = () => "Cr";

export const AnalysisScreen = ({ selectedIcons = [], onDone }) => {
  const [phase, setPhase] = useState(0);
  const counterRef = useRef(null);
  const unitRef = useRef(null);
  const containerRef = useRef(null);
  const flashRef = useRef(null);
  const iconsRef = useRef(null);
  const progressRef = useRef(null);
  const pulseRef = useRef(null);

  // Compute random durations once on mount — adds an emotional, never-the-same pacing.
  // Total tuned to ~5.5–7.5s across all phases.
  const durationsRef = useRef({
    phase1Entrance: rand(0.8, 1.1),
    phase1Settle: rand(0.4, 0.55),
    phase2: rand(2.2, 3.2), // counter scramble window
    phase3: rand(1.0, 1.4),
    phase4: rand(0.95, 1.25),
    phase5Flash: rand(0.18, 0.24),
    phase5Out: rand(0.35, 0.5),
  });

  useEffect(() => {
    const d = durationsRef.current;
    let scrambleInterval = null;

    const ctx = gsap.context(() => {
      const tl = gsap.timeline({
        onComplete: () => onDone?.(),
      });

      // ───── Phase 1: icons converge ─────
      tl.fromTo(
        iconsRef.current?.children || [],
        {
          opacity: 0,
          scale: 0.6,
          x: () => gsap.utils.random(-120, 120),
          y: () => gsap.utils.random(-80, 80),
        },
        {
          opacity: 1,
          scale: 1,
          x: 0,
          y: 0,
          duration: d.phase1Entrance,
          ease: "power3.out",
          stagger: 0.05,
        }
      )
        .to(iconsRef.current?.children || [], {
          scale: 0.5,
          opacity: 0.7,
          duration: d.phase1Settle,
          stagger: 0.03,
          ease: "power2.in",
        })

        // ───── Phase 2: counter SCRAMBLES (does not reveal real number) ─────
        .to({}, {
          duration: 0.05,
          onComplete: () => {
            setPhase(1);
            // Start scrambling on the next tick once counterRef mounts
            requestAnimationFrame(() => {
              scrambleInterval = setInterval(() => {
                if (counterRef.current) {
                  counterRef.current.textContent = randomScrambleValue();
                }
                if (unitRef.current) {
                  unitRef.current.textContent = randomScrambleUnit();
                }
              }, 90);
            });
          },
        })
        .to({}, {
          duration: d.phase2,
          onComplete: () => {
            if (scrambleInterval) {
              clearInterval(scrambleInterval);
              scrambleInterval = null;
            }
          },
        })

        // ───── Phase 3: inflation bar ─────
        .to({}, { duration: 0.05, onComplete: () => setPhase(2) })
        .fromTo(
          progressRef.current,
          { scaleX: 0 },
          { scaleX: 1, duration: d.phase3, ease: "power2.inOut" }
        )

        // ───── Phase 4: pulse ─────
        .to({}, { duration: 0.05, onComplete: () => setPhase(3) })
        .fromTo(
          pulseRef.current,
          { scale: 0.7, opacity: 0.6 },
          {
            scale: 1.6,
            opacity: 0,
            duration: d.phase4,
            ease: "power2.out",
            repeat: 1,
            repeatDelay: -0.4,
          }
        )

        // ───── Phase 5: flash & out ─────
        .to(flashRef.current, {
          opacity: 0.18,
          duration: d.phase5Flash,
          ease: "power2.in",
        })
        .to(flashRef.current, {
          opacity: 0,
          duration: d.phase5Out,
          ease: "power2.out",
        })
        .to(
          containerRef.current,
          { opacity: 0, scale: 0.97, duration: 0.4, ease: "power2.in" },
          "-=0.2"
        );
    });

    return () => {
      if (scrambleInterval) clearInterval(scrambleInterval);
      ctx.revert();
    };
  }, [onDone]);

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-[60] flex flex-col items-center justify-center bg-zinc-50 dark:bg-zinc-950 px-6 overflow-hidden"
      data-testid="analysis-screen"
    >
      {/* Ambient glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[70vw] h-[70vw] max-w-[600px] max-h-[600px] bg-brand/[0.08] dark:bg-brand/[0.06] blur-[120px] rounded-full pointer-events-none" />
      <div
        ref={pulseRef}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[40vw] h-[40vw] max-w-[400px] max-h-[400px] rounded-full bg-zinc-900/[0.03] dark:bg-white/[0.04] pointer-events-none"
      />

      {/* Flash overlay */}
      <div
        ref={flashRef}
        className="absolute inset-0 bg-zinc-900 dark:bg-white opacity-0 pointer-events-none"
      />

      {/* Floating icons (phase 1) */}
      {phase === 0 && (
        <div ref={iconsRef} className="flex items-center gap-3 mb-8">
          {selectedIcons.map((Icon, i) => (
            <div
              key={i}
              className="h-12 w-12 md:h-14 md:w-14 rounded-2xl bg-white border-zinc-200 text-zinc-900 dark:bg-zinc-900 dark:border-white/10 dark:text-zinc-50 border flex items-center justify-center"
            >
              <Icon className="h-5 w-5 md:h-6 md:w-6" strokeWidth={1.75} />
            </div>
          ))}
        </div>
      )}

      {/* Scrambling counter (phase 2) — never reveals the real figure */}
      {phase === 1 && (
        <div className="text-center relative z-10">
          <p className="text-xs tracking-[0.24em] uppercase text-zinc-500 dark:text-zinc-500 mb-4">
            Crunching the numbers…
          </p>
          <div
            className="font-display text-5xl sm:text-6xl md:text-8xl font-medium tracking-tighter text-zinc-900 dark:text-zinc-50 leading-none tabular-nums"
            data-testid="analysis-counter"
          >
            <span ref={counterRef}>₹—</span>
            <span
              ref={unitRef}
              className="text-2xl sm:text-3xl md:text-5xl text-zinc-500 dark:text-zinc-500 ml-3 align-baseline"
            >
              {""}
            </span>
          </div>
        </div>
      )}

      {/* Inflation bar (phase 3) */}
      {phase === 2 && (
        <div className="w-full max-w-sm text-center relative z-10">
          <p className="text-xs tracking-[0.24em] uppercase text-zinc-500 dark:text-zinc-500 mb-6">
            Adjusting for inflation
          </p>
          <div className="h-[3px] bg-zinc-200 dark:bg-zinc-900 rounded-full overflow-hidden">
            <div
              ref={progressRef}
              className="h-full bg-brand dark:bg-brand origin-left shadow-[0_0_20px_rgb(var(--brand-glow) / 0.5)]"
              style={{ transform: "scaleX(0)" }}
            />
          </div>
        </div>
      )}

      {/* Pulse text (phase 4) */}
      {phase === 3 && (
        <div className="text-center relative z-10">
          <div className="h-2 w-2 rounded-full bg-brand dark:bg-brand mx-auto mb-5 shadow-[0_0_20px_rgb(var(--brand-glow) / 0.8)] animate-pulse" />
        </div>
      )}

      <p
        key={phase}
        className="text-center text-base md:text-xl text-zinc-700 dark:text-zinc-300 font-medium tracking-tight max-w-md font-display mt-6 relative z-10 animate-in fade-in duration-500"
        data-testid="analysis-status-text"
      >
        {PHASES[phase].text}
      </p>
      <p className="text-xs md:text-sm text-zinc-500 mt-2 relative z-10">
        {PHASES[phase].sub}
      </p>
    </div>
  );
};
